import { useState, useEffect } from "react";
import UnifiedDatePicker from "../../components/UnifiedDatePicker";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  editVersionAction,
  fetchData,
  editVersion,
} from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faToggleOn,
  faToggleOff,
  faCodeBranch,
  faCalendarPlus,
  faHourglassEnd,
  faMessage,
  faRobot,
  faCalendarDays,
  faBullhorn,
  faStar,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import Can from "../../permissions/Can";
import Tooltip from "../../components/Tooltip";

export default function PublishNewVersion() {
  const [versionNumber, setVersionNumber] = useState("");
  const [dayNote, setDayNote] = useState("");
  const [releaseDate, setReleaseDate] = useState(new Date());
  const [releaseDate2, setReleaseDate2] = useState(new Date());
  const [is64Only, setIs64Only] = useState(0);
  const [schoolYear, setSchoolYear] = useState("");
  const [geminiModel, setGeminiModel] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const incrementVersion = (version) => {
    if (!version) return "1.0.0";
    const parts = version.split(".");
    if (parts.length === 3) {
      const major = parseInt(parts[0]) || 0;
      const minor = parseInt(parts[1]) || 0;
      const patch = parseInt(parts[2]) || 0;
      if (patch >= 9) return `${major}.${minor + 1}.0`;
      return `${major}.${minor}.${patch + 1}`;
    } else if (parts.length === 2) {
      const major = parseInt(parts[0]) || 0;
      const minor = parseInt(parts[1]) || 0;
      return `${major}.${minor + 1}`;
    } else {
      return `${parseInt(parts[0]) + 1}.0.0`;
    }
  };

  const decrementVersion = (version) => {
    if (!version) return "1.0.0";
    const parts = version.split(".");
    if (parts.length === 3) {
      const major = parseInt(parts[0]) || 0;
      const minor = parseInt(parts[1]) || 0;
      const patch = parseInt(parts[2]) || 0;
      if (patch <= 0) {
        if (minor <= 0) return `${Math.max(1, major - 1)}.9.9`;
        return `${major}.${minor - 1}.9`;
      }
      return `${major}.${minor}.${patch - 1}`;
    } else if (parts.length === 2) {
      const major = parseInt(parts[0]) || 0;
      const minor = parseInt(parts[1]) || 0;
      if (minor <= 0) return `${Math.max(1, major - 1)}.9`;
      return `${major}.${minor - 1}`;
    } else {
      return `${Math.max(1, parseInt(parts[0]) - 1)}.0.0`;
    }
  };

  const alfaresVersionData = useSelector(
    (state) => state.main.data?.alfaresVersionData
  )?.[0];
  const alfaresNoteData = useSelector(
    (state) => state.main.data?.alfaresNoteData
  )?.[0];
  const expirydateData = useSelector(
    (state) => state.main.data?.expirydateData
  )?.[0];

  useEffect(() => {
    if (!alfaresVersionData || !alfaresNoteData || !expirydateData) {
      dispatch(fetchData());
    }
  }, [dispatch, alfaresVersionData, alfaresNoteData, expirydateData]);

  useEffect(() => {
    if (alfaresVersionData) {
      setVersionNumber(alfaresVersionData.newprogver);
      setReleaseDate(new Date(alfaresVersionData.verupdate));
      setIs64Only(alfaresVersionData["64only"] || 0);
      setSchoolYear(alfaresVersionData.school_year || "");
      setGeminiModel(alfaresVersionData.gemini_model || "");
    }
    if (alfaresNoteData) setDayNote(alfaresNoteData.dayNote);
    if (expirydateData) setReleaseDate2(new Date(expirydateData.expirydate));
  }, [alfaresVersionData, alfaresNoteData, expirydateData]);

  const handleToggle64Only = () => {
    setIs64Only((prev) => (prev === 1 ? 0 : 1));
  };

  const handlePublishVersion = async () => {
    const result = await dispatch(
      editVersionAction({
        number: versionNumber,
        date: releaseDate,
        text: dayNote,
        date2: releaseDate2,
        only64: is64Only ? 1 : 0,
        school_year: schoolYear,
        gemini_model: geminiModel,
      })
    );

    if (editVersion.fulfilled.match(result)) {
      toast.success(result.payload?.message || "تم نشر الإصدار بنجاح");
    } else {
      toast.error("فشل في تحديث البيانات");
    }
  };

  return (
    <div className="publish-wrapper anim-fade-in" style={{ padding: "10px 0" }}>
      <div className="premium-edit-container publish-container">
        {/* Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={faBullhorn} />
            </div>
            <div className="header-text-box">
              <h2>نشر تحديث جديد</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">إدارة الإصدارات</span>
              </div>
            </div>
          </div>

          {/* Action Buttons in Header */}
          <div className="header-tools">
            <button
              className="premium-action-btn"
              onClick={() => navigate("/whats-new")}
              style={{ background: "#d97706" }}
            >
              <FontAwesomeIcon icon={faStar} />
              <span>جديد الإصدار</span>
            </button>
          </div>
        </div>

        {/* Form Body */}
        <div className="premium-form" style={{ padding: "24px 30px" }}>
          <div className="section-grid publish-grid">
            {/* رقم الإصدار */}
            <div className="input-field span-2">
              <label>رقم الإصدار الجديد</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faCodeBranch} className="field-icon" />
                <input
                  type="text"
                  value={versionNumber}
                  onChange={(e) => setVersionNumber(e.target.value)}
                  placeholder="x.x.x"
                  className="version-input"
                  dir="ltr"
                />
                <div className="version-controls">
                  <Tooltip content="تقليل الإصدار">
                    <button
                      type="button"
                      className="ver-btn minus"
                      onClick={() =>
                        setVersionNumber(decrementVersion(versionNumber))
                      }
                   
                    >
                      <i className="fa-solid fa-minus" />
                    </button>
                  </Tooltip>
                  <Tooltip content="زيادة الإصدار">
                    <button
                      type="button"
                      className="ver-btn plus"
                      onClick={() =>
                        setVersionNumber(incrementVersion(versionNumber))
                      }
                   
                    >
                      <i className="fa-solid fa-plus" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* العام الدراسي */}
            <div className="input-field">
              <label>العام الدراسي</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faCalendarDays} className="field-icon" />
                <input
                  type="text"
                  value={schoolYear}
                  onChange={(e) => setSchoolYear(e.target.value)}
                  placeholder="مثال: 2024-2025"
                  dir="ltr"
                />
              </div>
            </div>

            {/* تاريخ الإصدار */}
            <div className="input-field">
              <label>تاريخ الإصدار الجديد</label>
              <div className="input-wrapper date-wrapper">
                <FontAwesomeIcon icon={faCalendarPlus} className="field-icon" />
                <UnifiedDatePicker
                  value={
                    releaseDate ? releaseDate.toISOString().split("T")[0] : ""
                  }
                  onChange={(date) =>
                    setReleaseDate(date ? new Date(date) : null)
                  }
                  placeholder="اختر تاريخ الإصدار"
                  portalId="root"
                />
              </div>
            </div>

            {/* تاريخ انتهاء الفارس */}
            <div className="input-field">
              <label>تاريخ نهاية الفارس</label>
              <div className="input-wrapper date-wrapper">
                <FontAwesomeIcon icon={faHourglassEnd} className="field-icon" />
                <UnifiedDatePicker
                  value={
                    releaseDate2 ? releaseDate2.toISOString().split("T")[0] : ""
                  }
                  onChange={(date) =>
                    setReleaseDate2(date ? new Date(date) : null)
                  }
                  placeholder="اختر تاريخ الانتهاء"
                  portalId="root"
                />
              </div>
            </div>

            {/* موديل الذكاء الاصطناعي */}
            <div className="input-field">
              <label>موديل الذكاء الاصطناعي</label>
              <div className="input-wrapper">
                <FontAwesomeIcon icon={faRobot} className="field-icon" />
                <input
                  type="text"
                  value={geminiModel}
                  onChange={(e) => setGeminiModel(e.target.value)}
                  placeholder="مثال: gemini-1.5-flash"
                  dir="ltr"
                />
              </div>
            </div>

            {/* الرسالة الترحيبية */}
            <div className="input-field span-3">
              <label>الرسالة الترحيبية للشاشة الرئيسية</label>
              <div className="input-wrapper textarea-wrapper">
                <FontAwesomeIcon
                  icon={faMessage}
                  className="field-icon textarea-icon"
                />
                <textarea
                  value={dayNote}
                  onChange={(e) => setDayNote(e.target.value)}
                  placeholder="أدخل نص الرسالة الترحيبية..."
                  rows={3}
                />
              </div>
            </div>

            {/* تبديل 64-بت فقط */}
            <div className="input-field span-3">
              <div
                className="premium-toggle-card"
                onClick={handleToggle64Only}
                style={{ padding: "14px 20px", borderRadius: "14px" }}
              >
                <div className="toggle-info" style={{ gap: "15px" }}>
                  <FontAwesomeIcon
                    icon={is64Only ? faToggleOn : faToggleOff}
                    className={`toggle-icon ${is64Only ? "active" : ""}`}
                    style={{ fontSize: "30px" }}
                  />
                  <div className="toggle-text">
                    <span className="main-label">64-بت فقط</span>
                    <span className="sub-label">
                      {is64Only
                        ? "مفعّل — مخصص للأجهزة 64 بت فقط"
                        : "معطّل — يعمل على جميع الأجهزة"}
                    </span>
                  </div>
                </div>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: is64Only ? "var(--success)" : "var(--muted)",
                    background: is64Only
                      ? "color-mix(in srgb, var(--success) 10%, transparent)"
                      : "var(--panel)",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    border: `1px solid ${
                      is64Only ? "var(--success)" : "var(--border)"
                    }`,
                    transition: "all 0.3s",
                  }}
                >
                  {is64Only ? "مفعّل" : "معطّل"}
                </span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions" style={{ marginTop: "20px" }}>
            <Can module="versions" action="add">
              <button
                type="button"
                className="premium-submit-btn"
                style={{ height: "52px", borderRadius: "14px" }}
                onClick={handlePublishVersion}
              >
                <div className="btn-content">
                  <FontAwesomeIcon icon={faRotateRight} />
                  <span>نشر وتحديث البيانات</span>
                </div>
                <div className="btn-glow" />
              </button>
            </Can>
          </div>
        </div>
      </div>

      <style>{`
        .publish-wrapper {
          padding: 5px !important;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
        }

        .publish-container {
          margin: 0;
          width: 100% !important;
          max-width: none !important;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          box-shadow: none;
        }

        /* ── Header ── */
        .premium-header-modern {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; 
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px 6px 0 0;
          gap: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        /* ── Form Content ── */
        .premium-form {
          background: var(--panel);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 6px 6px;
          padding: 20px !important;
        }
        .header-left { display: flex; align-items: center; gap: 12px; }

        .header-icon-box {
          width: 40px; height: 40px; border-radius: 6px; background: var(--accent);
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; color: white;
          flex-shrink: 0;
        }

        .header-text-box h2 { margin: 0; font-weight: 400; font-size: 17px; font-family: 'Cairo', sans-serif; }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); margin-top: 2px; font-size: 12px; }
        .breadcrumb-info span:first-child { color: #d97706; font-weight: 400; }
        .separator { margin: 0 4px; }
        .current-page { color: var(--accent); font-weight: 400; }

        .header-tools { display: flex; align-items: center; gap: 10px; }

        .premium-action-btn {
          border: none; border-radius: 6px; padding: 10px 18px; color: white;
          display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 400;
          cursor: pointer; transition: 0.2s; font-family: 'Cairo', sans-serif; white-space: nowrap;
        }
        .premium-action-btn:hover { transform: translateY(-1px); filter: brightness(1.05); }

        /* ── Grid ── */
        .publish-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .span-2 { grid-column: span 2; }
        .span-3 { grid-column: span 3; }

        /* ── Fields ── */
        .input-field {
          min-width: 0;
          width: 100%;
        }
        .input-field label {
          display: block;
          font-size: 13px;
          font-weight: 400;
          margin-bottom: 6px;
          color: var(--muted);
          font-family: 'Cairo', sans-serif;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .field-icon {
          position: absolute;
          right: 12px;
          color: var(--muted);
          font-size: 14px;
          opacity: 0.6;
          z-index: 1;
          pointer-events: none;
        }

        .input-wrapper input,
        .input-wrapper textarea {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 0 38px 0 12px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
          outline: none;
        }

        .input-wrapper input { height: 44px; }

        .input-wrapper input:focus,
        .input-wrapper textarea:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 10%, transparent);
          background: var(--panel);
        }

        /* Version number special styling */
        .version-input {
          text-align: center !important;
          font-size: 17px !important;
          font-weight: 400 !important;
          color: #C5172E !important;
          letter-spacing: 2px;
          padding-left: 80px !important;
          font-family: 'Cairo', sans-serif;
        }

        .version-controls {
          position: absolute;
          left: 5px;
          display: flex;
          gap: 4px;
        }

        .ver-btn {
          border: none;
          border-radius: 6px;
          width: 34px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 12px;
          color: white;
          transition: all 0.2s;
        }
        .ver-btn.minus { background: #1976D2; }
        .ver-btn.plus  { background: #2196F3; }
        .ver-btn:hover { filter: brightness(1.1); }

        /* Textarea */
        .textarea-wrapper { align-items: flex-start; width: 100%; }
        .textarea-icon { top: 12px !important; position: absolute; }
        .input-wrapper textarea {
          height: 90px;
          padding: 10px 38px 10px 12px;
          resize: vertical;
          border-radius: 6px;
        }

        /* Date picker wrapper */
        .date-wrapper .unified-date-picker,
        .date-wrapper > div { width: 100%; }

        /* Toggle card */
        .premium-toggle-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 12px 14px;
          cursor: pointer;
          transition: 0.2s;
          width: 100%;
          box-sizing: border-box;
        }
        .premium-toggle-card:hover { border-color: var(--accent); }
        .toggle-info { display: flex; align-items: center; gap: 10px; }
        .toggle-icon { color: var(--muted); transition: 0.2s; flex-shrink: 0; }
        .toggle-icon.active { color: var(--accent); }
        .toggle-text { display: flex; flex-direction: column; gap: 2px; }
        .main-label { font-weight: 400; color: var(--text); font-size: 14px; font-family: 'Cairo', sans-serif; }
        .sub-label { color: var(--muted); font-size: 11px; }

        /* Submit Button */
        .premium-submit-btn {
          width: 100%;
          background: var(--accent);
          border: none;
          border-radius: 6px;
          padding: 14px;
          color: white;
          font-size: 15px;
          font-weight: 400;
          cursor: pointer;
          transition: 0.2s;
          box-shadow: 0 2px 8px color-mix(in srgb, var(--accent) 30%, transparent);
          font-family: 'Cairo', sans-serif;
        }
        .btn-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .premium-submit-btn:hover { background: #1d7567; }

        @media (max-width: 900px) {
          .premium-header-modern {
            padding: 12px 14px;
            gap: 10px;
            flex-wrap: wrap;
          }
          .header-left { gap: 10px; }
          .header-icon-box { width: 38px; height: 38px; font-size: 16px; }
          .header-text-box h2 { font-size: 16px; }
          .breadcrumb-info { font-size: 11px; }
          .header-tools { gap: 8px; width: 100%; }
          .premium-action-btn {
            padding: 8px 12px;
            font-size: 12px;
            white-space: nowrap;
          }
        }

        @media (max-width: 768px) {
          .publish-wrapper { padding: 0 !important; }
          .premium-form { padding: 14px 12px !important; }
          .publish-grid { grid-template-columns: 1fr !important; gap: 12px; }
          .span-2, .span-3 { grid-column: span 1; }
          .publish-header-actions { margin-right: 0; width: 100%; }
          .input-field label { font-size: 13px; }
          .premium-submit-btn { font-size: 14px; padding: 13px; }
        }
      `}</style>
    </div>
  );
}
